import { FC, Suspense } from "react";
import { useAtomValue } from "jotai";

import { Alert, AlertDescription, Surface, Text } from "@zapier/design-system";
import {
  nodesAtom,
  edgesAtom,
  canvasAtom,
  zslAtom,
  zapsAtom_SUSPENDS,
  ZapMetadata,
} from "@zapier/canvas-state";

import { CopyToClipboardTextarea } from "../../components/CopyToClipboardTextarea";

import styles from "./DevTools.module.css";

type Data = any;
const serialize = (data: Data) => JSON.stringify(data, null, 2);

const isNotEmpty = (obj: object) => Object.keys(obj).length > 0;

function getZapCount(zaps: Record<string, ZapMetadata>): number {
  return Object.keys(zaps).length;
}

const SuspendedZapsSection: FC = () => {
  const zaps = useAtomValue(zapsAtom_SUSPENDS);
  const rawZapsData = serialize(zaps);
  const hasZaps = isNotEmpty(zaps);

  if (!hasZaps) {
    return null;
  }

  const zapCount = getZapCount(zaps);

  return (
    <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
      <div className={styles.devtoolsInner}>
        <Text type="Body3">Zaps ({zapCount})</Text>
        <CopyToClipboardTextarea value={rawZapsData} />
      </div>
    </Surface>
  );
};

export const CanvasTabContent: FC = () => {
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    edges: canvasEdges,
    nodes: canvasNodes,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...rest
  } = useAtomValue(canvasAtom);
  const zsl = useAtomValue(zslAtom);
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);

  const rawCanvasData = serialize(rest);
  const rawNodesData = serialize(nodes);
  const rawEdgesData = serialize(edges);
  const rawZslData = zsl ? serialize(zsl) : undefined;

  const hasNodes = nodes.length > 0;
  const hasEdges = edges.length > 0;

  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert title="" status="info" variant="compact">
        <AlertDescription>
          <Text type="MinimalPrint3">
            Canvas data. You can use this to debug the Canvas.
          </Text>
        </AlertDescription>
      </Alert>
      {hasNodes && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Nodes ({nodes.length})</Text>
            <CopyToClipboardTextarea value={rawNodesData} />
          </div>
        </Surface>
      )}
      {hasEdges && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Edges ({edges.length})</Text>
            <CopyToClipboardTextarea value={rawEdgesData} />
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Canvas</Text>
          <CopyToClipboardTextarea value={rawCanvasData} />
        </div>
      </Surface>
      <Suspense fallback={null}>
        <SuspendedZapsSection />
      </Suspense>
      {rawZslData && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">ZSL</Text>
            <CopyToClipboardTextarea value={rawZslData} />
          </div>
        </Surface>
      )}
    </div>
  );
};
